import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Eye, Truck, AlertTriangle, ExternalLink } from 'lucide-react';
import API from '../../../util/API';
import ADMIN_PATHS from '../ADMIN_PATHS';

const cx = (...arr) => arr.filter(Boolean).join(' ');

const Card = ({ children, className }) => (
  <div className={cx('bg-white border border-slate-200 rounded-2xl', className)}>{children}</div>
);

const SectionHead = ({ title, subtitle, right }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="h-4 w-[3px] rounded-full bg-primary" aria-hidden="true" />
        <h2 className="text-base md:text-lg font-black tracking-tight text-slate-900">{title}</h2>
      </div>
      {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
    </div>
    {right ? <div className="shrink-0">{right}</div> : null}
  </div>
);

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

// Same detection the backend forwarding idempotency check uses
// (features/aliexpress/aePlaceOrder.service → isAliExpressOrderPlaced), minus
// the AliExpressTracking-map signal (every place/approve path records
// externalOrderIds or items.ali_order_id on success).
const hasAnyAliOrderId = (item) => Boolean(String(item?.ali_order_id || '').trim());

const isPushedToAliExpress = (order) =>
  (Array.isArray(order?.externalOrderIds) && order.externalOrderIds.length > 0) ||
  (Array.isArray(order?.items) ? order.items : []).some(hasAnyAliOrderId);

const collectAliOrderIds = (order) => {
  const ids = [];
  (Array.isArray(order?.externalOrderIds) ? order.externalOrderIds : []).forEach((id) => {
    const value = String(id || '').trim();
    if (value && !ids.includes(value)) ids.push(value);
  });
  (Array.isArray(order?.items) ? order.items : []).forEach((item) => {
    const value = String(item?.ali_order_id || '').trim();
    if (value && !ids.includes(value)) ids.push(value);
  });
  return ids;
};

const AliStatusPill = ({ status }) => {
  const s = String(status || '').toLowerCase();
  const cls =
    s === 'placed'
      ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
      : s === 'failed'
        ? 'border-rose-200 bg-rose-50 text-rose-700'
        : 'border-slate-200 bg-slate-50 text-slate-700';
  const label = s === 'placed' ? 'Placed' : s === 'failed' ? 'Failed' : 'No AE status';

  return (
    <span className={cx('inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-black', cls)}>
      {label}
    </span>
  );
};

const TabButton = ({ active, label, count, onClick, icon: Icon }) => (
  <button
    type="button"
    onClick={onClick}
    className={cx(
      'inline-flex items-center gap-2 rounded-2xl border px-4 py-2 text-sm font-bold transition-colors',
      active
        ? 'border-primary/30 bg-primary/10 text-primary'
        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
    )}
    aria-pressed={active}
  >
    <Icon className="w-4 h-4" />
    {label}
    <span
      className={cx(
        'rounded-full px-2 py-0.5 text-xs font-black',
        active ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-600'
      )}
    >
      {count}
    </span>
  </button>
);

const AliExpressForwarding = () => {
  const [awaiting, setAwaiting] = useState([]);
  const [pushed, setPushed] = useState([]);
  const [activeTab, setActiveTab] = useState('awaiting'); // awaiting | pushed
  const [pageStatus, setPageStatus] = useState('idle'); // idle | loading | failed | succeeded
  const [loadError, setLoadError] = useState('');

  const loadAll = useCallback(async () => {
    setPageStatus('loading');
    setLoadError('');
    try {
      const [awaitingRes, allRes] = await Promise.all([
        API.get('/order/awaiting-aliexpress'),
        API.get('/order/admin'),
      ]);

      const awaitingData = Array.isArray(awaitingRes?.data?.data) ? awaitingRes.data.data : [];
      setAwaiting(awaitingData);

      const allOrders = Array.isArray(allRes?.data) ? allRes.data : [];
      setPushed(allOrders.filter(
        (o) => o?.isPaid && String(o?.order_status || '') !== 'cancelled' && isPushedToAliExpress(o)
      ));
      setPageStatus('succeeded');
    } catch (err) {
      setAwaiting([]);
      setPushed([]);
      setPageStatus('failed');
      setLoadError(
        err?.response?.data?.error
        || err?.response?.data?.message
        || err?.message
        || 'Failed to load AliExpress forwarding queues'
      );
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const pushedRows = useMemo(() => {
    return (Array.isArray(pushed) ? pushed : [])
      .sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0))
      .map((order) => ({
        key: String(order?._id || order?.orderId || ''),
        orderId: String(order?.orderId || order?._id || '—'),
        createdAt: order?.createdAt,
        totalPrice: Number(order?.totalPrice || 0),
        currency: String(order?.currency || 'USD'),
        itemCount: (Array.isArray(order?.items) ? order.items : []).length,
        externalStatus: order?.externalStatus || null,
        externalErrorCode: order?.externalErrorCode || null,
        externalErrorMessage: order?.externalErrorMessage || null,
        aliOrderIds: collectAliOrderIds(order),
      }));
  }, [pushed]);

  const awaitingRows = useMemo(() => {
    return (Array.isArray(awaiting) ? awaiting : []).map((order) => ({
      key: String(order?._id || order?.orderId || ''),
      orderId: String(order?.orderId || order?._id || '—'),
      createdAt: order?.createdAt,
      totalPrice: Number(order?.totalPrice || 0),
      currency: String(order?.currency || 'USD'),
      itemCount: Number(order?.itemCount || 0),
      itemNames: Array.isArray(order?.itemNames) ? order.itemNames : [],
    }));
  }, [awaiting]);

  const head = (
    <SectionHead
      title="AliExpress Forwarding"
      subtitle="Real paid + approved orders and their AliExpress placement state."
      right={
        <button
          type="button"
          onClick={loadAll}
          disabled={pageStatus === 'loading'}
          className={cx(
            'inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-primary',
            pageStatus === 'loading' ? 'opacity-60 cursor-not-allowed' : ''
          )}
        >
          <RefreshCw className={cx('w-4 h-4', pageStatus === 'loading' ? 'animate-spin' : '')} />
          Refresh
        </button>
      }
    />
  );

  const note = (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-3 text-xs text-slate-600">
      Data source: GET /order/awaiting-aliexpress + GET /order/admin (client-side
      externalOrderIds / items.ali_order_id split). This page is a read-only queue —
      Forward / Retry actions live on each order’s detail page (AliExpress placement
      requires the order-scoped admin endpoints).
    </div>
  );

  if (pageStatus === 'failed') {
    return (
      <div className="p-6 space-y-6">
        {head}
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
          {loadError}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {head}
      {note}

      <div className="flex flex-wrap items-center gap-2">
        <TabButton
          active={activeTab === 'awaiting'}
          label="Awaiting forwarding"
          count={awaitingRows.length}
          onClick={() => setActiveTab('awaiting')}
          icon={Truck}
        />
        <TabButton
          active={activeTab === 'pushed'}
          label="Forwarded"
          count={pushedRows.length}
          onClick={() => setActiveTab('pushed')}
          icon={ExternalLink}
        />
      </div>

      {activeTab === 'awaiting' ? (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Order ID</th>
                  <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Items</th>
                  <th className="px-4 py-3 text-right text-xs font-black tracking-wide text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageStatus === 'loading' ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">
                      Loading awaiting-forwarding queue…
                    </td>
                  </tr>
                ) : awaitingRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">
                      Nothing waiting for AliExpress. Paid + approved orders get pushed on approval;
                      retry the ones that failed from their detail page.
                    </td>
                  </tr>
                ) : (
                  awaitingRows.map((order) => (
                    <tr key={order.key} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-mono text-sm font-black text-primary">{order.orderId}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{formatDateTime(order.createdAt)}</td>
                      <td className="px-4 py-3 text-sm font-bold text-slate-900">
                        {order.currency} {order.totalPrice.toFixed(2)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-slate-900">{order.itemCount} item(s)</p>
                        {order.itemNames.length ? (
                          <p className="mt-0.5 max-w-[320px] truncate text-xs text-slate-500" title={order.itemNames.join(', ')}>
                            {order.itemNames.join(', ')}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Link
                          to={`${ADMIN_PATHS.ORDERS_PAGES.DETAILS}/${order.key}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Open (Forward here)
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Order ID</th>
                  <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">AE status</th>
                  <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">AE Order IDs</th>
                  <th className="px-4 py-3 text-right text-xs font-black tracking-wide text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageStatus === 'loading' ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                      Loading forwarded orders…
                    </td>
                  </tr>
                ) : pushedRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                      No paid orders have been forwarded to AliExpress yet.
                    </td>
                  </tr>
                ) : (
                  pushedRows.map((order) => (
                    <tr key={order.key} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-mono text-sm font-black text-primary">{order.orderId}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{formatDateTime(order.createdAt)}</td>
                      <td className="px-4 py-3 text-sm font-bold text-slate-900">
                        {order.currency} {order.totalPrice.toFixed(2)}
                      </td>
                      <td className="px-4 py-3">
                        <AliStatusPill status={order.externalStatus} />
                        {order.externalStatus === 'failed' ? (
                          <p className="mt-1 flex items-start gap-1 max-w-[280px] text-xs text-rose-700" title={order.externalErrorMessage || ''}>
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            <span className="line-clamp-2">
                              {order.externalErrorCode ? `${order.externalErrorCode}: ` : ''}
                              {order.externalErrorMessage || 'Forwarding failed.'}
                            </span>
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-700">
                        {order.aliOrderIds.length ? (
                          <span className="block max-w-[220px] truncate" title={order.aliOrderIds.join(', ')}>
                            {order.aliOrderIds.join(', ')}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Link
                          to={`${ADMIN_PATHS.ORDERS_PAGES.DETAILS}/${order.key}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View order
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AliExpressForwarding;
